/** Fallback image when no thumbnail exists (Academy 1.0 card.svg) */
export const CARD_FALLBACK_IMAGE =
  `https://images.contentstack.io/v3/assets/${process.env.NEXT_PUBLIC_API_KEY}/blt4bca940e8221c667/65e9b89d008e6616a6e014b3/card.svg`;

/**
 * Resolves thumbnail/image URL from a catalog or CMS entry.
 * Handles Contentstack asset shapes: thumbnail/og image as string, { url }, or { file: { url } }.
 */
function urlFromAsset(asset: unknown): string {
  if (!asset) return '';
  if (typeof asset === 'string') return asset;
  const o = asset as Record<string, unknown>;
  if (typeof o?.url === 'string') return o.url;
  const file = o?.file as Record<string, unknown> | undefined;
  if (typeof file?.url === 'string') return file.url;
  return '';
}

/**
 * Resolves thumbnail/image URL from a catalog or search (Swiftype) entry.
 * Handles: thumbnail, open_graph.image, and Swiftype’s top-level image (Academy 1.0 search).
 */
export function getEntryThumbnailUrl(entry: any): string {
  const thumb = urlFromAsset(entry?.thumbnail);
  if (thumb) return thumb;
  const ogImage = urlFromAsset(entry?.open_graph?.image);
  if (ogImage) return ogImage;
  const image = urlFromAsset(entry?.image);
  if (image) return image;
  return '';
}

/** Returns true if course has a real thumbnail (not fallback) */
export function hasCourseThumbnail(course: {
  thumbnail?: { url?: string };
  open_graph?: { image?: { url?: string } | null };
} | null | undefined): boolean {
  const thumbUrl = course?.thumbnail?.url;
  if (thumbUrl && typeof thumbUrl === 'string' && thumbUrl.trim() !== '') return true;
  const ogUrl = course?.open_graph?.image?.url;
  return !!(ogUrl && typeof ogUrl === 'string' && ogUrl.trim() !== '');
}

/** Get course thumbnail URL with fallback when missing or invalid */
export function getCourseThumbnailUrl(course: {
  thumbnail?: { url?: string };
  open_graph?: { image?: { url?: string } | null };
  title?: string;
} | null | undefined): string {
  const thumbUrl = course?.thumbnail?.url;
  if (thumbUrl && typeof thumbUrl === 'string' && thumbUrl.trim() !== '') return thumbUrl;
  const ogUrl = course?.open_graph?.image?.url;
  if (ogUrl && typeof ogUrl === 'string' && ogUrl.trim() !== '') return ogUrl;
  return CARD_FALLBACK_IMAGE;
}

/** Get course thumbnail src for Image (adds ?width=500 for Contentstack URLs, not for SVG fallback) */
export function getCourseThumbnailSrc(course: {
  thumbnail?: { url?: string };
  open_graph?: { image?: { url?: string } | null };
} | null | undefined): string {
  const url = getCourseThumbnailUrl(course);
  return url.includes('card.svg') ? url : `${url}?width=800`;
}
